export interface DetailedBullet {
  title: string
  detail: string
}

export interface Bonus {
  id: string
  title: string
  subtitle: string
  /** Reference value in USD cents, shown struck through next to "Included free" */
  referenceValueCents?: number
  intro?: string
  bulletsIntro?: string
  bullets?: string[]
  detailedBullets?: DetailedBullet[]
  secondaryBulletsIntro?: string
  secondaryBullets?: string[]
  secondaryNote?: string
  systemLabel?: string
  system?: string
  result?: string
  closing?: string[]
}

export const bonuses: Bonus[] = [
  {
    id: '01',
    title: 'Nursing Skills & Vital Signs Flashcards',
    subtitle: 'Fast, Accurate Recall Before Every Skills Lab and Clinical Shift',
    referenceValueCents: 14990,
    intro:
      'Printable flashcards to drill the essentials every nursing student must know cold, with a clear visual on the front and the answer and teaching notes on the back.',
    bulletsIntro: 'Includes:',
    bullets: [
      'Adult vital-sign reference ranges',
      'Pulse & auscultation sites',
      'Glasgow Coma Scale scoring',
      'Edema & pulse grading scales',
      'PPE donning & doffing order',
      'WHO 5 Moments of hand hygiene',
      'Patient positions & oxygen devices',
      'Injection angles, sites & medication rights',
      'Beginner, standard & advanced decks',
    ],
    result: 'Help students recall the core nursing essentials quickly and accurately.',
  },
  {
    id: '02',
    title: 'Clinical Skills Demonstration Lesson Scripts',
    subtitle: 'Run a Polished Skills Demonstration Without Preparing From Scratch',
    referenceValueCents: 9990,
    intro:
      'Word-for-word lesson scripts for core nursing skills, each with talking points, the 4-step demonstration sequence, what students must observe, common errors to correct, return-demonstration practice and a quick check.',
    bulletsIntro: 'Includes:',
    bullets: [
      'Hand hygiene & PPE',
      'Vital signs',
      'Head-to-toe assessment',
      'Bed bath & patient positioning',
      'Safe transfers with a gait belt',
      'Sterile gloving & dressing change',
      'Catheter care & oxygen setup',
      'IV tubing priming & the medication pass',
    ],
    result: 'Teach every demonstration with the confidence of a script that already works.',
  },
  {
    id: '03',
    title: 'Skills Lab & Simulation Drills',
    subtitle: 'Keep Every Lab Session Active, Practical and Safe',
    referenceValueCents: 7990,
    intro:
      'Short, ready-to-run drills for skills labs and simulation rooms, each with timing (5–20 minutes), materials, setup, steps, evaluation points and easy / standard / advanced variations.',
    bulletsIntro: 'Includes:',
    bullets: [
      'Blood pressure accuracy challenges',
      'PPE relay',
      'Sterile-field contamination spotting',
      'Patient identification & allergy checks',
      'Fall-risk room hunts',
      'SBAR handoff role-plays',
      '“Who do you see first?” prioritization rounds',
      'Rapid-response mini scenarios & timed medication passes',
    ],
    result: 'Fill every lab session with practice that moves students forward.',
  },
  {
    id: '04',
    title: 'Dosage Calculation Practice Workbook',
    subtitle: 'Build Safe, Confident Calculation Skills Step by Step',
    referenceValueCents: 6990,
    intro:
      'A printable dosage-calculation workbook that progresses from beginner to standard to advanced, with fully worked solutions, common student errors, sanity-check rules and timed quizzes with answer keys.',
    bulletsIntro: 'Includes:',
    bullets: [
      'Unit conversions',
      'Desired ÷ Have × Quantity: tablets, liquids & injectables',
      'Weight-based dosing',
      'IV pump rates in mL/h',
      'Gravity drip rates in gtt/min',
      'IV piggyback & infusion-time problems',
      'Reading syringes & medication labels',
    ],
    result: 'Help students calculate every dose with a clear, safe method.',
  },
  {
    id: '05',
    title: 'Nursing Competency Checklists, Rubrics & Certificates',
    subtitle: 'Evaluate Objectively and Document Every Competency Achieved',
    referenceValueCents: 5990,
    intro:
      'Ready-to-use skills check-off checklists with critical steps, clinical evaluation rubrics scored on clear beginner, standard and advanced criteria, OSCE station sheets, clinical requirement trackers, student progress logs and elegant completion certificates.',
    bulletsIntro: 'Includes:',
    bullets: [
      'Hand hygiene & PPE',
      'Vital signs & head-to-toe assessment',
      'Sterile dressing change',
      'Catheter care & oxygen therapy',
      'IV fundamentals',
      'Medication administration',
      'Clinical judgment & safety rubrics',
      'Communication, professionalism & certificates',
    ],
    result: 'Assess every student fairly and give structured feedback at every level.',
  },
]
